// src/layout/AppLayout.tsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "antd";
import Sidebar from "./Sidebar";
import HeaderComponent from "./Header";
import { Outlet } from "react-router-dom";
import { customThemes } from "../themes/themeConfig";
import { useDispatch } from "react-redux";
import { logout } from "../redux/slice/userSlice";

import useBillingShortcuts from "../hooks/useBillingShortcuts";
const { Content, Footer } = Layout;

const AppLayout: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useBillingShortcuts();
  // Logout handler
  const handleLogout = () => {
    dispatch(logout());
    navigate("/login");
  };

  const [collapsed, setCollapsed] = useState<boolean>(false);
  const [darkMode, setDarkMode] = useState<boolean>(false);

  const themeStyles = darkMode ? customThemes.dark : customThemes.light;

  // ✅ Media query hook
  const useMediaQuery = (query: string) => {
    const [matches, setMatches] = useState<boolean>(false);

    useEffect(() => {
      const media = window.matchMedia(query);
      setMatches(media.matches);

      const listener = () => setMatches(media.matches);
      media.addEventListener("change", listener);

      return () => media.removeEventListener("change", listener);
    }, [query]);

    return matches;
  };

  const isMobile = useMediaQuery("(max-width: 768px)");

  // Auto collapse sidebar on mobile
  useEffect(() => {
    if (isMobile) {
      setCollapsed(true);
    }
  }, [isMobile]);

  return (
    <Layout style={{ minHeight: "100vh" }}>
      {/* SIDEBAR */}
      <Sidebar
        collapsed={collapsed}
        darkMode={darkMode}
        themeStyles={themeStyles}
        isMobile={isMobile}
      />

      <Layout>
        {/* HEADER */}
        <HeaderComponent
          collapsed={collapsed}
          setCollapsed={setCollapsed}
          darkMode={darkMode}
          setDarkMode={setDarkMode}
          themeStyles={themeStyles}
          isMobile={isMobile}
        />

        {/* MAIN CONTENT */}
        <Content
          style={{
            padding: "16px",
            minHeight: 360,
            backgroundColor: themeStyles.contentBg,
            color: themeStyles.contentText,
          }}
        >
          <Outlet context={{ darkMode, themeStyles }} />
        </Content>

        {/* FOOTER */}
        <Footer
          style={{
            textAlign: "center",
            padding: "16px 0",
            backgroundColor: themeStyles.footerBg,
            color: themeStyles.contentText,
          }}
        >
          CRM ©{new Date().getFullYear()}
        </Footer>
      </Layout>
    </Layout>
  );
};

export default AppLayout;